import axios from 'axios';
import {
    RO_ID,
    RO_NAME,
    RO_APP_TITLE,
    RO_REFRESHED_AT,
    RO_COMPANY_URL,
    RO_CONTACT_PAGE_URL,
    RO_LOGO_URL,
    RO_FAVICON_URL,
    RO_ICON_96x96_URL,
    RO_ICON_180x180_URL,
    RO_ICON_192x192_URL,
    RO_ICON_512x512_URL,
    RO_THEME_LIGHT_PRIMARY_MAIN,
    RO_THEME_LIGHT_PRIMARY_DARK,
    RO_THEME_LIGHT_PRIMARY_LIGHT,
    RO_THEME_LIGHT_BACKGROUND_DEFAULT,
    RO_THEME_DARK_PRIMARY_MAIN,
    RO_THEME_DARK_PRIMARY_DARK,
    RO_THEME_DARK_PRIMARY_LIGHT,
    RO_THEME_DARK_BACKGROUND_DEFAULT,
    RO_FIREBASE_API_KEY,
    RO_FIREBASE_AUTH_DOMAIN,
    RO_FIREBASE_PROJECT_ID,
    RO_FIREBASE_STORAGE_BUCKET,
    RO_FIREBASE_MESSAGING_SENDER_ID,
    RO_FIREBASE_APP_ID,
    RO_FIREBASE_MEASUREMENT_ID,
} from '../pages/configuration/actions';

// Tenant (root organization) white-label configuration: fetch, cache, expose.
// One appUrl-keyed request (replaced the legacy two-step client-credentials
// bootstrap). The response includes the
// tenant's Cognito app client id (client_id — one app client per root
// organization); the backend is the ONLY source for it: no build-time
// fallback, so a tenant without one fails visibly (TENANT_NOT_PROVISIONED).

const TENANT_CONFIG_KEY = 'tenant_config';
const TENANT_CONFIG_TTL_MS = 24 * 60 * 60 * 1000;

const RETRY_DELAYS_MS = [0, 1000, 3000];

const tenantConfigUrl = () =>
    process.env.REACT_APP_TENANT_CONFIG_URL ||
    `${process.env.REACT_APP_AUTH_URL}/organizations/root/app-url`;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Public endpoint: no credentials of any kind.
const requestOnce = async () => {
    const response = await axios.get(tenantConfigUrl(), {
        params: { appUrl: window.location.origin, includes: 'logos,firebase,theme' },
    });
    return response.data;
};

// The app is unusable without these; anything else degrades to defaults at
// the consumer (palette.jsx color fallbacks, default favicon, push disabled).
const REQUIRED_FIELDS = ['root_organization_id', 'name', 'app_title', 'theme_light_primary_main'];

// Error code: the tenant exists but has no Cognito app client id yet.
export const TENANT_NOT_PROVISIONED = 'TENANT_NOT_PROVISIONED';

const normalize = (data) => {
    if (!data) throw new Error('Empty tenant config response');
    const missing = REQUIRED_FIELDS.filter((field) => !data[field]);
    if (missing.length) {
        throw new Error(`Tenant config missing required fields: ${missing.join(', ')}`);
    }
    if (!data.client_id) {
        const error = new Error('Tenant config has no Cognito client_id');
        error.code = TENANT_NOT_PROVISIONED;
        throw error;
    }
    return { fetched_at: new Date().toISOString(), ...data };
};

export const fetchTenantConfig = async () => {
    let lastError;
    for (const delay of RETRY_DELAYS_MS) {
        if (delay) await sleep(delay);
        try {
            return normalize(await requestOnce());
        } catch (error) {
            lastError = error;
            const status = error?.response?.status;
            // Unknown domain / tenant not provisioned — retrying will not help
            if (status === 404 || error?.code === TENANT_NOT_PROVISIONED) break;
        }
    }
    throw lastError;
};

export const loadTenantConfig = () => {
    try {
        const raw = localStorage.getItem(TENANT_CONFIG_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

export const isTenantConfigStale = (config) => {
    const fetchedAt = config?.fetched_at ? Date.parse(config.fetched_at) : 0;
    return !fetchedAt || Date.now() - fetchedAt > TENANT_CONFIG_TTL_MS;
};

// The Cognito app client id of this tenant, from the cached backend config
// only (see TenantBootstrap: the app does not render in Cognito mode without it).
export const getTenantClientId = () => loadTenantConfig()?.client_id || null;

export const saveTenantConfig = (config) => {
    localStorage.setItem(TENANT_CONFIG_KEY, JSON.stringify(config));

    // Legacy adapter: mirror the config into the individual RO_* keys that the
    // rest of the app still reads synchronously (palette.jsx at module scope,
    // the manifest/favicon effect in App.js, RO_ID data consumers). Only
    // defined values are written — a missing optional field falls back to the
    // reader's own default instead of bricking the tenant. Remove once all
    // readers consume tenant_config directly.
    const legacyValues = {
        [RO_ID]: config.root_organization_id,
        [RO_NAME]: config.name,
        [RO_APP_TITLE]: config.app_title,
        [RO_THEME_LIGHT_PRIMARY_MAIN]: config.theme_light_primary_main,
        [RO_THEME_LIGHT_PRIMARY_DARK]: config.theme_light_primary_dark,
        [RO_THEME_LIGHT_PRIMARY_LIGHT]: config.theme_light_primary_light,
        [RO_THEME_LIGHT_BACKGROUND_DEFAULT]: config.theme_light_background_default,
        [RO_THEME_DARK_PRIMARY_MAIN]: config.theme_dark_primary_main,
        [RO_THEME_DARK_PRIMARY_DARK]: config.theme_dark_primary_dark,
        [RO_THEME_DARK_PRIMARY_LIGHT]: config.theme_dark_primary_light,
        [RO_THEME_DARK_BACKGROUND_DEFAULT]: config.theme_dark_background_default,
        [RO_LOGO_URL]: config.connect_header_logo?.src,
        [RO_FAVICON_URL]: config.connect_favicon_logo?.src,
        [RO_ICON_96x96_URL]: config.connect_icon96x96_png?.src,
        [RO_ICON_180x180_URL]: config.connect_icon180x180_png?.src,
        [RO_ICON_192x192_URL]: config.connect_icon192x192_png?.src,
        [RO_ICON_512x512_URL]: config.connect_icon512x512_png?.src,
        [RO_COMPANY_URL]: config.company_url,
        [RO_CONTACT_PAGE_URL]: config.contact_page_url,
        [RO_FIREBASE_API_KEY]: config.firebase_api_key,
        [RO_FIREBASE_AUTH_DOMAIN]: config.firebase_auth_domain,
        [RO_FIREBASE_PROJECT_ID]: config.firebase_project_id,
        [RO_FIREBASE_STORAGE_BUCKET]: config.firebase_storage_bucket,
        [RO_FIREBASE_MESSAGING_SENDER_ID]: config.firebase_messaging_sender_id,
        [RO_FIREBASE_APP_ID]: config.firebase_app_id,
        [RO_FIREBASE_MEASUREMENT_ID]: config.firebase_measurement_id,
    };
    Object.entries(legacyValues).forEach(([key, value]) => {
        if (value != null) localStorage.setItem(key, value);
    });

    // Keeps the App.js render gate (and instant rollback to the legacy
    // bootstrap) working.
    localStorage.setItem(RO_REFRESHED_AT, config.fetched_at);
};
